export type ProfiledNavigationType =
  | PerformanceNavigationTiming['type']
  | 'push'
  | 'replace'
  | 'traverse'
  | 'unknown';

export type PageTiming = {
  documentLoad: boolean;
  navigationType: ProfiledNavigationType;
  startedAt: number;
};

type ClientNavigationTiming = PageTiming & { pathname: string };

const PAGE_TIMING_KEY = Symbol.for('iconai.page-timing');

function timingStore(): Record<PropertyKey, unknown> | undefined {
  if (typeof window === 'undefined') return undefined;

  return window as unknown as Record<PropertyKey, unknown>;
}

/** Records the start of a Next.js client-side route transition. */
export function markPageNavigationStart(
  url: string,
  navigationType: 'push' | 'replace' | 'traverse'
): void {
  const store = timingStore();

  if (!store) return;

  store[PAGE_TIMING_KEY] = {
    documentLoad: false,
    navigationType,
    pathname: new URL(url, window.location.href).pathname,
    startedAt: performance.now(),
  } satisfies ClientNavigationTiming;
}

/** Returns the timing origin for the current document or client navigation. */
export function getPageTiming(): PageTiming {
  const store = timingStore();

  if (!store) {
    return { documentLoad: true, navigationType: 'navigate', startedAt: 0 };
  }

  const clientTiming = store[PAGE_TIMING_KEY] as
    | ClientNavigationTiming
    | undefined;

  if (clientTiming?.pathname === window.location.pathname) return clientTiming;

  const navigation =
    typeof performance.getEntriesByType === 'function'
      ? (performance.getEntriesByType('navigation')[0] as
          | PerformanceNavigationTiming
          | undefined)
      : undefined;

  if (navigation) {
    try {
      if (new URL(navigation.name).pathname !== window.location.pathname) {
        return {
          documentLoad: false,
          navigationType: 'unknown',
          startedAt: performance.now(),
        };
      }
    } catch {
      // A malformed navigation name should fall through to document timing.
    }
  }

  return {
    documentLoad: true,
    navigationType: navigation?.type ?? 'navigate',
    startedAt: 0,
  };
}
